import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const PixelHeart = ({ className = "w-8 h-8" }) => {
  const grid = [
    ". . 1 1 1 . . . 1 1 1 . .",
    ". 1 2 2 2 1 . 1 2 2 2 1 .",
    "1 3 2 2 2 2 1 2 2 4 4 2 1",
    "1 3 2 2 2 2 2 2 2 2 4 2 1",
    "1 3 3 2 2 2 2 2 2 2 2 2 1",
    ". 1 3 3 2 2 2 2 2 2 2 1 .",
    ". . 1 3 3 2 2 2 2 2 1 . .",
    ". . . 1 3 3 2 2 2 1 . . .",
    ". . . . 1 3 3 2 1 . . . .",
    ". . . . . 1 3 1 . . . . .",
    ". . . . . . 1 . . . . . ."
  ];
  const colors = {
    '1': '#000000',
    '2': '#ed1c24',
    '3': '#a61e1c',
    '4': '#ffffff',
  };
  return (
    <svg viewBox="0 0 13 11" className={className} shapeRendering="crispEdges">
      {grid.map((row, y) => 
        row.split(' ').map((cell, x) => 
          cell !== '.' ? <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill={colors[cell]} /> : null
        )
      )}
    </svg>
  );
};

// Функция для перемешивания массива
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function Quiz() {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const file = params.get('file') || 'questions-react.json';

    const loadQuestions = async () => {
      try {
        const response = await fetch(`/data/${file}`);
        const data = await response.json();
        
        // Проверяем, есть ли сохраненный прогресс
        const savedProgress = localStorage.getItem(`quiz_progress_${file}`);
        if (savedProgress) {
          const parsed = JSON.parse(savedProgress);
          if (parsed.questions && parsed.questions.length === 15) {
            setQuestions(parsed.questions);
            setCurrentQuestion(parsed.currentQuestion);
            setScore(parsed.score);
            setLives(parsed.lives !== undefined ? parsed.lives : 3);
            setIsLoading(false);
            return;
          }
        }
        
        // Выбираем только 15 случайных вопросов из пула
        const shuffledQuestions = shuffleArray(data.questions).slice(0, 15);
        setQuestions(shuffledQuestions);
        localStorage.setItem(`quiz_progress_${file}`, JSON.stringify({
          currentQuestion: 0,
          score: 0,
          lives: 3,
          questions: shuffledQuestions
        }));
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading questions:', error);
      }
    };

    loadQuestions();
  }, [location]);

  const handleAnswer = (index) => {
    setSelectedAnswer(index);
    const correct = index === questions[currentQuestion].correct;
    setIsCorrect(correct);

    let newScore = score;
    let newLives = lives;
    if (correct) {
      newScore = score + 1;
      setScore(newScore);
    } else {
      newLives = lives - 1;
      setLives(newLives);
    }

    setIsAnswered(true);

    setTimeout(() => {
      setIsAnswered(false);
      setSelectedAnswer(null);
      const nextQuestion = currentQuestion + 1;
      
      const params = new URLSearchParams(location.search);
      const file = params.get('file') || 'questions-react.json';

      if (newLives <= 0) {
        localStorage.removeItem(`quiz_progress_${file}`);
        navigate('/results', { state: { score: newScore, total: questions.length, isGameOver: true } });
      } else if (nextQuestion < questions.length) {
        setCurrentQuestion(nextQuestion);
        localStorage.setItem(`quiz_progress_${file}`, JSON.stringify({
          currentQuestion: nextQuestion,
          score: newScore,
          lives: newLives,
          questions
        }));
      } else {
        localStorage.removeItem(`quiz_progress_${file}`);
        navigate('/results', { state: { score: newScore, total: questions.length } });
      }
    }, 2000);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-black text-xl md:text-2xl animate-pulse tracking-widest">LOADING...</p>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="w-full">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 border-4 border-black p-4 bg-acid shadow-[8px_8px_0_0_#000]">
          <div className="flex justify-between items-center mb-4 text-xs md:text-sm text-black font-bold">
            <span>STAGE {currentQuestion + 1}/{questions.length}</span>
            <div className="flex gap-1 md:gap-2">
              {[...Array(3)].map((_, i) => (
                <PixelHeart 
                  key={i} 
                  className={`w-4 h-4 md:w-6 md:h-6 transition-all duration-300 ${i < lives ? '' : 'opacity-20 grayscale'}`} 
                />
              ))}
            </div>
            <div className="text-sm md:text-base font-bold uppercase tracking-widest text-black flex items-center">
              SCORE: 
              {score > 0 ? (
                <span key={score} className="inline-block animate-score-pop ml-2">
                  {score * 100}
                </span>
              ) : (
                <span className="inline-block ml-2">{score * 100}</span>
              )}
            </div>
          </div>
          <div className="w-full bg-acid border-4 border-black h-8 p-1">
            <div
              className="bg-black h-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="border-4 border-black bg-acid p-6 md:p-8 shadow-[12px_12px_0_0_#000]">
          <h2 className="text-sm md:text-xl font-bold text-black mb-8 leading-relaxed">
            {questions[currentQuestion].text}
          </h2>

          <div className="grid gap-6">
            {questions[currentQuestion].options.map((option, index) => {
              if (isAnswered && selectedAnswer !== index && index !== questions[currentQuestion].correct) {
                return null;
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  className={`p-4 border-4 text-xs md:text-sm text-left transition-none uppercase ${isAnswered
                    ? index === questions[currentQuestion].correct
                      ? 'border-black bg-black text-acid'
                      : index === selectedAnswer
                        ? 'border-black bg-white text-black line-through'
                        : 'hidden'
                    : 'border-black text-black hover:bg-black hover:text-acid active:bg-black active:text-acid shadow-[4px_4px_0_0_#000] hover:translate-y-1 hover:translate-x-1 hover:shadow-none active:translate-y-1 active:translate-x-1 active:shadow-none'
                    }`}
                  disabled={isAnswered}
                >
                  <span className="mr-4">[{['A','B','C','D'][index]}]</span> {option}
                </button>
              );
            })}
          </div>

          {isAnswered && (
            <div className={`mt-8 border-t-4 border-dashed border-black pt-8 text-center uppercase tracking-widest text-black`}>
              <div className={`inline-block px-6 py-4 border-4 border-black ${isCorrect ? 'animate-spin-jump bg-acid shadow-[4px_4px_0_0_#000]' : 'animate-glitch bg-black text-acid shadow-[4px_4px_0_0_var(--color-primary)]'}`}>
                <p className="text-xl md:text-2xl font-bold flex items-center justify-center gap-2">
                  {isCorrect ? <>SUCCESS! <PixelHeart className="w-8 h-8" /></> : '( ERROR 404 )'}
                </p>
                {isCorrect ? (
                   <p className="text-xs mt-2 font-bold animate-pulse">+100 HITS</p>
                ) : (
                   <p className="text-xs mt-2 font-bold">SYSTEM FAILURE</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Quiz;
